import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaTools, FaCalendar, FaComment, FaAngleDown, FaRulerCombined, FaClock, FaTachometerAlt } from 'react-icons/fa';
import { FaLocationDot, FaHouseChimney } from 'react-icons/fa6';

import axios from "axios";

const API_PATH = 'https://roofguard-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    serviceType: '',
    roofMaterial: '',
    roofArea: '',
    address: '',
    date: '',
    time: '',
    comment: '',
    extraServices: []
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.serviceType) validationErrors.push('Service type is required');
      if (!formData.roofMaterial) validationErrors.push('Roof material is required');
      if (!formData.roofArea) validationErrors.push('Roof area is required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.date) validationErrors.push('Date is required');
      if (!formData.time) validationErrors.push('Time is required');
      if (!formData.comment) validationErrors.push('Message is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => postData.append(`${key}[]`, item));
        } else {
          postData.append(key, value);
        }
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        serviceType: '',
        roofMaterial: '',
        roofArea: '',
        address: '',
        date: '',
        time: '',
        comment: '',
        extraServices: []
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, extraServices: [...prev.extraServices, value] };
      } else {
        return { ...prev, extraServices: prev.extraServices.filter(item => item !== value) };
      }
    });
  };

  return (
    <section className="custom-form get-quote">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Request A Quote'} subTitle={"Get Your Roofing Estimate"} linear sideHeading icon={<FaHouseChimney />} />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Service Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="serviceType"><FaTools /></label>
                  <select
                    className="select-box"
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Service Type</option>
                    <option value="Roof Repair">Roof Repair</option>
                    <option value="Roof Installation">Roof Installation</option>
                    <option value="Roof Maintenance">Roof Maintenance</option>
                    <option value="Roof Replacement">Roof Replacement</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">

              {/* Roofing Material Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="roofMaterial"><FaTachometerAlt /></label>
                  <select
                    className="select-box"
                    id="roofMaterial"
                    name="roofMaterial"
                    value={formData.roofMaterial}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Roof Material</option>
                    <option value="Asphalt Shingles">Asphalt Shingles</option>
                    <option value="Metal Roofing">Metal Roofing</option>
                    <option value="Tile Roofing">Tile Roofing</option>
                    <option value="Wood Shingles">Wood Shingles</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Roof Area Input Field */}
              <div className="input-field">
                <label htmlFor="roofArea"><FaRulerCombined /></label>
                <input
                  type="number"
                  name="roofArea"
                  className="box"
                  id="roofArea"
                  placeholder="Roof Area (sq ft)"
                  value={formData.roofArea}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Address Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaLocationDot /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input
                  type="text"
                  name="date"
                  placeholder="Date"
                  id="date"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
    
              {/* Time Input Field */}
              <div className="input-field">
                <label htmlFor="time"><FaClock /></label>
                <input
                  type="text"
                  name="time"
                  placeholder="Time"
                  id="time"
                  className="box"
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Comment Text Area */}
            <div className="input-field text-area">
              <label htmlFor="comment"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="comment"
                className="box"
                id="comment"
                placeholder="Message"
                value={formData.comment}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Extra Services Checkboxes */}
            <div className="extra-services">

              {/* Installation Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="roof-installation"
                  name="extraServices[]"
                  value="Installation"
                  checked={formData.extraServices.includes('Installation')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="roof-installation">Installation</label>
                <div className="checkbox-indicator"></div>
              </div>
        
              {/* Roof Removal Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="roof-removal"
                  name="extraServices[]"
                  value="Roof Removal"
                  checked={formData.extraServices.includes('Roof Removal')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="roof-removal">Roof Removal</label>
                <div className="checkbox-indicator"></div>
              </div>
        
              {/* Gutter Installation Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="gutter-installation"
                  name="extraServices[]"
                  value="Gutter Installation"
                  checked={formData.extraServices.includes('Gutter Installation')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="gutter-installation">Gutter Installation</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;